// El motor de la tabla de Clientes: TanStack Table con sus tres modelos client-side sobre las filas
// ACUMULADAS por `useInfiniteQuery`. El filtro, el orden y las páginas trabajan sobre lo que ya
// llegó del servidor, y la pantalla lo declara mientras queden páginas (ESTANDAR-TABLAS §5.4).
//
// La tabla se PINTA con el `DataTable` compartido, que es el chasis del mock; esto solo decide qué
// filas y en qué orden.
import { useMemo } from 'react';
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type OnChangeFn,
    type SortingState,
    type Table,
} from '@tanstack/react-table';
import {
    compareSortValues,
    matchesFilters,
    sortValueOf,
    type CreditCustomerRow,
    type CreditCustomersFilters,
    type SortKey,
    type SortState,
} from '../lib/creditCustomersFilters';

/** Filas por página de lo YA cargado. Es la paginación que el analista controla con los botones. */
export const TABLE_PAGE_SIZE = 25;

/** Una columna del motor por cada clave de orden. El pintado vive en `CreditCustomersTable`. */
const SORT_KEYS: SortKey[] = [
    'id',
    'name',
    'level',
    'sci',
    'activeCredits',
    'balance',
    'status',
    'lastPayment',
];

/** El filtro corre UNA vez por fila: solo esta columna se declara alcanzable por la búsqueda global. */
const GLOBAL_FILTER_COLUMN: SortKey = 'name';

export interface CreditCustomersTableModel {
    table: Table<CreditCustomerRow>;
    /** Las filas de la página que se está viendo, con referencia estable entre renders. */
    pageRows: CreditCustomerRow[];
    /** Cuántas filas pasan los filtros, sobre TODO lo cargado. No es el total del padrón. */
    filteredCount: number;
}

export function useCreditCustomersTable(input: {
    /** Todas las páginas del servidor aplanadas. Tiene que venir memoizada por quien llama. */
    rows: CreditCustomerRow[];
    filters: CreditCustomersFilters;
    sort: SortState;
    onSortChange: (next: SortState) => void;
}): CreditCustomersTableModel {
    const { rows, filters, sort, onSortChange } = input;

    // Referencia estable: `useReactTable` compara `columns` por referencia igual que `data`.
    const columns = useMemo<ColumnDef<CreditCustomerRow>[]>(
        () =>
            SORT_KEYS.map((key) => ({
                id: key,
                accessorFn: (row) => sortValueOf(row, key),
                enableGlobalFilter: key === GLOBAL_FILTER_COLUMN,
                sortingFn: (left, right) =>
                    compareSortValues(
                        sortValueOf(left.original, key),
                        sortValueOf(right.original, key),
                    ),
            })),
        [],
    );

    const sorting = useMemo<SortingState>(
        () => [{ id: sort.key, desc: sort.direction === 'desc' }],
        [sort],
    );

    const onSortingChange: OnChangeFn<SortingState> = (updater) => {
        const next = typeof updater === 'function' ? updater(sorting) : updater;
        if (next.length === 0) return;
        onSortChange({ key: next[0].id as SortKey, direction: next[0].desc ? 'desc' : 'asc' });
    };

    const table = useReactTable({
        data: rows,
        columns,
        state: { sorting, globalFilter: filters },
        onSortingChange,
        // El estado del filtro es el objeto de filtros entero: la regla vive en `matchesFilters`,
        // que es pura y la comparten los conteos de la barra.
        globalFilterFn: (row, _columnId, value: CreditCustomersFilters) =>
            matchesFilters(row.original, value, value.query.trim().toLowerCase()),
        getColumnCanGlobalFilter: (column) => column.id === GLOBAL_FILTER_COLUMN,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: TABLE_PAGE_SIZE } },
    });

    const modelRows = table.getRowModel().rows;
    const pageRows = useMemo(() => modelRows.map((row) => row.original), [modelRows]);

    return { table, pageRows, filteredCount: table.getFilteredRowModel().rows.length };
}
